import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from 'gatsby';
import { Spacing, Text, TextLink } from '@dls/web';
import { scrollToSection } from '@common-modules/product-catalogue';
import {
  accessoryActions,
  addonsActions,
  api,
  cartActions,
  checkoutActions,
  eshopCartActions,
  fulFillmentActions,
  orderActions,
  userActions,
  voucherActions
} from '@detox/actions';
import { trans as t } from '../../helpers/localisation';
import { ShoppingCart as ShoppingCartModule } from '@common-modules/shopping-cart';
import { getCookie, getUrlParams } from '@lux/helpers';
import GenericError from './GenericError';
import CPModal from '../Checkout/CPModals';
import {
  createShoppingCartDataModelVoucher,
  filterModelByBrand,
  getAccessoryToRemove,
  getUpfront,
  isBibAvailable,
  isMobileShareOrSimOnlyPlan,
  getAccessoriesRecommender,
  validateTradeIn,
  getAccessoriesItemsList,
  getApplicableVoucher
} from '../../helpers/shoppingCartHelper';
import {
  defaultGenericErrorModal,
  getCartError,
  getCartErrorDataModel,
  getShoppingCartDataModel,
  overrideRemovableAddons
} from '../../helpers/cart';
import {
  getAddonIdsByProductSpecContainmentID,
  getTradeInSelection
} from '../../helpers/addon';
import {
  dataLayerPush,
  flattenNodes,
  getAemConfigs,
  formatServiceNumberForPxAddons,
  capitalizeFirstChar
} from '../../helpers/common';
import createDescription from '../../helpers/create-description';
import useResetVoucher from '../../hooks/useResetVoucher';
import { CART, REQUEST_STATUS, VOUCHER_ACTION } from '../../constants';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import {
  CP9_FLOW,
  RED_PHASE,
  TRADEIN,
  VOUCHER
} from '../../types/featureFlag.types';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import { getUserInfo } from '../../reducers/user';
import { isSimOnlyIPP } from '../../reducers/plan';
import {
  navigateToPlansPage,
  navigateToProductDetailsPage
} from '../../helpers/navigation';
import useStockCheck, { STOCK_CHECK_TYPE } from '../../hooks/useStockCheck';
import OutOfStock from './OutOfStock';
import { CollectAtStoreModal } from './CollectAtStoreModal';
import { PRODUCT, ORDER } from '../../constants';
import { CREDIT_LIMIT_INDICATOR } from '../CreditLimitRetrieved/constants';
import { AEM_CONFIG_KEYS, CP_ERROR_CODES } from '../../config/common';
import { getCreditLimitErrorProps } from '../../helpers/credit-limit';
import CCEMisMatchError from '../CCEPrompt/CCEMisMatchErrorModal';
import getIsRoiMismatch from '../../helpers/is-roi-mismatch';
import { useAmendedTab } from '../../hooks/useAmendedTab/useAmendedTab';
import { getShoppingCartDataLayer } from '../../helpers/dataLayerHelpers';
import { useBrowseBackPopup } from '../../hooks/useBrowseBackPopup';
import { getUIAMLoginUrl } from '../../config';
import { navigation } from '../../middlewares/navigation-constants';
import { addonMcAfeeTripleCheck } from '../AddonCatalog/addon-helper';
import { getTradeInDetails } from '../../selectors';
import { checkAccessoryForFulfilment, formatDate } from '../Fulfilment/helpers';
import {
  clearCheckoutError,
  dispatchCheckoutExistingNew,
  dispatchCheckoutRecon,
  resetModifyAccessories,
  resetModifyAddons,
  resetVouchers,
  setAcknowledgeNotification,
  setBibDisplayData
} from '../../reducers/cart';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { CONTAINMENT_IDS } from '../../constants/addon';
import { useModal } from '../../hooks/useModal';
import { SkeletonType } from '../SkeletonLoader';
import { useLoader } from '../../hooks/useLoader';
import { getItemSkuToOrderIdMapping } from '../Checkout/helper';
import { useEkycInit } from '../../hooks/useEkycEnhancements';
import CONSTANTS from '../../constants/checkout';
import CONSTANT from '../../constants/common';
import { ACTION_TYPES } from '../../constants/actions';
import { AbFeatureNames } from '../../constants/ab-feature-names';
import { useABTesting } from '@wec-core/ab-testing';
import { BaseState } from '../../types/state.types';
import { UserInfo } from '../../types/user.types';
import { ShoppingCartState } from '../../types/shoppingCart.types';
import { isSkuAvailable } from '../../reducers/fulfillment/mtposStock';
import { pxAddonsCalculateDispatch, updateAddons } from '../../reducers/addons';
import { clearDeliveryData } from '../../reducers/fulfillment/delivery';
import { ShoppingCartAccessories } from './ShoppingCartAccessories';
import {
  getAccessorySkusToCheckStock,
  getRecommendedAccessories
} from '../../helpers/accessory';
import { SelectedAccessory } from '../../types/accessory.types';

const fetchOfflineStockApi = api.aem.offlineStockAvailability;
const eShopPxAddonsCalculatePayable =
  api.onlineApi.eShopPxAddonsCalculatePayable;
export const ShoppingCart = ({ data }) => {
  const {
    allAddonMobileLob,
    allAspireBillingOffer,
    allTradeIn,
    allErrorMessage,
    allPlan,
    allVoucherRedemption,
    allVariableConfig,
    fulfilment,
    allAccessory
  } = data;

  const { addTradeInToOrder, removeAddonFromOrder } = addonsActions;
  const { getShoppingCartData, emptyCart } = cartActions;
  const { checkout } = checkoutActions;
  const { isEBillMethod: getBillingPreference } = fulFillmentActions;
  const {
    updateOrderToRunRules,
    removeAccessoriesFromOrder,
    addAccessoriesFromOrder,
    updateMainLineNumber,
    addCustomerContact
  } = orderActions;
  const { getUserInformation, setSelectedService } = userActions;
  const {
    storeSelectedAccessories,
    clearSelectedAccessories
  } = accessoryActions;
  const { addVouchers, removeVouchers, getAllVouchers } = voucherActions;

  const {
    user,
    addons,
    cart,
    order,
    plan,
    product,
    promotions,
    voucher,
    accessory: accessoryState,
    checkout: checkoutState,
    iphone,
    userInfo,
    simOnlyIPP,
    isMtposStockAvailable,
    tradeInDetails
  } = useSelector<BaseState, Partial<ShoppingCartState>>(state => {
    return {
      ...state,
      simOnlyIPP: isSimOnlyIPP(state),
      userInfo: getUserInfo(state) as UserInfo,
      isMtposStockAvailable: isSkuAvailable(state),
      tradeInDetails: getTradeInDetails(state)
    };
  });
  const dispatch = useDispatch();
  const { tradeInCID: tradeInCIDValue } = tradeInDetails;
  const { contact, contactId, subscriberData, customerId } = userInfo;
  const cisUserInfo = user?.cis?.information;
  const checkoutFlow = checkoutState?.checkoutFlow;
  const { promoOrderDetails } = promotions;
  const { selectedAddons, availableAddons } = addons;
  const roiValidatedData = iphone.validateSkuData;
  const userInformation = user.information;
  const bib = cart?.order?.bib;
  const specialPromo = promotions.specialPromo;
  const btlOfferData = promotions.btlOfferData;

  const allVouchers = flattenNodes(allVoucherRedemption);
  const accessories = flattenNodes(allAccessory);
  const {
    siteMetadata: { siteUrl }
  } = data.site;
  const skipEmptyCartPage = useRef(false);
  const { productOrder, addedToContact } = order;
  const { cartOrder, acknowledgeNotification } = cart;
  const { selectedProduct } = product;
  const { selectedPlan } = plan;
  const [cceMismatch, setCceMismatch] = useState(false);
  const [offlineStockLoading, setOfflineStockLoading] = useState(false);
  const isSop = isSimOnlyPlan(selectedPlan);
  const variableConfigs = flattenNodes(allVariableConfig) || [];
  const { setModalData, renderedModal } = useModal();
  const pxAddons = useRef([]);
  pxAddons.current = addons.pxAddons;

  const {
    isFeatureAllowed: [
      abTestDeletePendingOrderForNewFlow,
      isNewBillingPreferenceApiEnabled,
      abTestEshopVoucherEnabled,
      abEshopOrderEnabled,
      enhancedErrorEnabled
    ],
    isPending: abTestApiPending
  } = useABTesting({
    featureName: [
      AbFeatureNames.DELETE_PENDING_ORDER_FOR_NEW_FLOW,
      AbFeatureNames.ESHOP_BILLING_PREFERENCE,
      AbFeatureNames.ESHOP_VOUCHER,
      AbFeatureNames.ESHOP_ORDER,
      AbFeatureNames.ENHANCED_ERROR_HANDLER
    ]
  });

  const [
    eligibleBibGroupIds = '',
    bibTnc = '',
    removeAddonIDs = '',
    enableAccessoriesForShoppingCart = '',
    pxBccAddonsMapper = ''
  ] = getAemConfigs(variableConfigs, [
    AEM_CONFIG_KEYS.DETOX_BIB_DEVICE_GROUP_ID,
    AEM_CONFIG_KEYS.DETOX_BIB_TNC,
    AEM_CONFIG_KEYS.DETOX_SHOPPING_CART_REMOVE_ADDON_IDS,
    AEM_CONFIG_KEYS.DETOX_ACCESSORIES_FOR_SHOPPING_CART,
    AEM_CONFIG_KEYS.DETOX_PX_BCC_ADDONS_MAPPER
  ]);

  const { renderError } = useErrorHandler({
    states: [addons, accessoryState],
    configs: {
      reloadOnClose: true,
      enhancedErrorHandler: enhancedErrorEnabled,
      suppressError: !enhancedErrorEnabled
    }
  });

  const applicableVouchers = useMemo(() => {
    return getApplicableVoucher({
      selectedPlan,
      selectedProduct,
      productOrder,
      cartOrder,
      voucher,
      allVouchers,
      eshopVoucherEnabled: abTestEshopVoucherEnabled
    });
  }, [
    voucher?.voucherByServiceId,
    abTestApiPending,
    abTestEshopVoucherEnabled,
    voucher.eshopVouchers
  ]);

  const {
    loading: onlineStockLoading,
    stockAvailable: {
      notAvailableSkus,
      status: onlineStockAvailable,
      stockDetails: { accessories: accessoryAvailability }
    }
  } = useStockCheck({
    accessorySkus: getAccessorySkusToCheckStock(
      getRecommendedAccessories(
        accessories,
        selectedProduct?.recommendedAccessories ?? []
      )
    ),
    type: STOCK_CHECK_TYPE.ALL,
    eshopMtposStockEnabled: false,
    abTestingPending: abTestApiPending
  });

  useEffect(() => {
    setBibData();
  }, [bib, bib?.tradeInBo?.productID]);

  useEffect(() => {
    const updateOrder = async () => {
      await dispatch(
        addCustomerContact({
          contact,
          customerId,
          productOrder,
          isAnonymous: true,
          showLoader: true
        })
      );
    };

    if (
      !btlOfferData &&
      !addedToContact &&
      contact &&
      customerId &&
      productOrder
    ) {
      updateOrder();
    }
  }, [addedToContact, contact, customerId, productOrder]);

  const [toastMessage, setToastMessage] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const isUserLoggedin = Boolean(user?.information?.clientContext);
  const [isMounted, setIsMounted] = useState(false);
  const plans = useMemo(() => flattenNodes(allPlan), [allPlan]);
  const urlParams = getUrlParams();

  useEffect(() => {
    if (
      !acknowledgeNotification &&
      voucher?.vouchers?.length > applicableVouchers.length
    ) {
      setToastMessage(t('NOTIFICATION_CANT_SEE_VOUCHER'));
    } else if (
      urlParams.creditLimitIndicator === CREDIT_LIMIT_INDICATOR.INCREASED
    ) {
      setToastMessage(t('CREDIT_LIMIT_SUCCESSFULL'));
    }
  }, [voucher?.vouchers, applicableVouchers, urlParams.creditLimitIndicator]);

  useEffect(() => {
    const getUser = async () => {
      if (!isUserLoggedin) {
        await dispatch(getUserInformation());
      }
    };

    getUser();
    dataLayerPush(
      getShoppingCartDataLayer({
        selectedPlan,
        selectedVariant: selectedProduct,
        accessories: accessoryState.selectedAccessories,
        location: window.location,
        order: order.productOrder
      }),
      true
    );
  }, []);

  const { renderAmendedErrorPopup } = useAmendedTab();

  const { brands, modelByBrand } = getTradeInSelection(
    flattenNodes(allTradeIn)[0]?.brands
  );

  const fetchVouchers = () => {
    if (abTestEshopVoucherEnabled) {
      return dispatch(eshopCartActions.getVoucher());
    }

    dispatch(getAllVouchers({ customerId, contactId }));
  };

  const fetchShoppingCart = useCallback(() => {
    const { productOrderId, productOrderReferenceNumber, type } = productOrder;

    let subscriberItem = '';
    if (
      user?.selectedService?.serviceId &&
      subscriberData &&
      subscriberData.length > 0
    ) {
      subscriberItem = subscriberData.find(
        item => item.primaryResource === user?.selectedService?.serviceId
      );
    }
    const productImage = selectedProduct?.imageGallery?.find(
      img => img.name === selectedProduct?.colour
    );

    const imageUrl = siteUrl + productImage?.images[0]?.src;

    dispatch(
      getShoppingCartData({
        productOrderId: productOrderId,
        productOrderReferenceNumber: productOrderReferenceNumber,
        technicalVasIds: allAddonMobileLob.edges.map(edge => edge.node.addonId),
        aspireBillingOffers: allAspireBillingOffer.edges.map(
          edge => edge.node.boId
        ),
        contactId: contactId,
        subscriberItem: subscriberItem,
        custType: cisUserInfo ? 'CIS' : 'RES',
        txn: type,
        deviceDisplayName: selectedProduct?.title,
        deviceCapacity: selectedProduct?.size,
        deviceColor: selectedProduct?.colour,
        deviceImagePath: imageUrl,
        tradeInProductSpecContainmentId,
        pxAddons: pxAddons.current,
        pxExistingAddons: addons.pxExistingAddons,
        pxBccAddonsMapper
      })
    );
  }, [
    allAddonMobileLob.edges,
    allAspireBillingOffer.edges,
    getShoppingCartData,
    productOrder,
    subscriberData
  ]);

  const cceProceedClicked = async () => {
    setCceMismatch(false);
    await handleEmptyCart();
    navigate('/', { replace: true });
  };

  const isForwardFromAddonPage = previousPage => {
    return previousPage === `/${navigation.ADDONS}`;
  };

  // Handle browse back - browse forward case
  const { resetLoginAndCheckoutData } = useBrowseBackPopup({
    callbackOnBackForwardPressed: async options => {
      if (isForwardFromAddonPage(options?.previousPage)) {
        await dispatch(
          updateOrderToRunRules({
            productId: productOrder?.productId,
            productOrderItemId: productOrder?.productOrderItemId,
            selectedAddonIds: addons.selectedAddons,
            promoOrderDetails: {
              ...promoOrderDetails,
              sourcePlan: user?.selectedService?.offeringId
            }
          })
        );
        fetchShoppingCart();
      }
      resetLoginAndCheckoutData();
    }
  });

  const tradeInProductSpecContainmentId =
    addons.containmentIdData?.[CONTAINMENT_IDS.TRADE_IN]
      ?.productSpecContainmentID;

  const bibCustomLiner = (
    <Text type="smallBody">
      {t('BIB_NEEDS_HELP_TEXT', {
        tnc: (
          <TextLink
            inline
            onClick={() => {
              window.open(bibTnc, '_blank');
            }}
          >
            {t('VIEW_TNC')}
          </TextLink>
        )
      })}
    </Text>
  );

  function setBibData() {
    if (bib) {
      const hasBibBo = !!bib.tradeInBo?.productID;
      dispatch(
        setBibDisplayData({
          title: t('BIB_TITLE'),
          subTittle: t('BIB_VALID_TILL', {
            date: formatDate(new Date(Number(bib.endDate)), 'dd MMM yyyy')
          }),
          status: '',
          imei: bib.imei,
          isApplied: hasBibBo,
          customLiner: bibCustomLiner
        })
      );
    }
  }

  useEffect(() => {
    if (contact) {
      dispatch(
        getBillingPreference({
          contactId: contact.contactId,
          nric: contact.indentValue,
          indentType: contact.indentType
        })
      );
    }
  }, [contact]);

  useEffect(() => {
    if (productOrder) {
      fetchShoppingCart();
    }
    setIsMounted(true);
  }, [fetchShoppingCart, productOrder]);

  useEffect(() => {
    if (contactId && customerId && abTestApiPending === false) {
      fetchVouchers();
    }

    return () => {
      dispatch(resetVouchers());
    };
  }, [contactId, customerId, abTestApiPending]);
  useResetVoucher();

  useEffect(() => {
    if (addons.isRemoveBibSuccess) {
      dispatch(
        setBibDisplayData({
          status: '',
          hintMessage: '',
          isApplied: false
        })
      );
    }
  }, [addons.isRemoveBibSuccess]);

  useEkycInit();
  useEffect(() => {
    if (cart.checkoutError) {
      handleEmptyCart();
    }
  }, [cart.checkoutError]);

  /* refresh quote data if anything is changed */
  useEffect(() => {
    if (voucher.modified || accessoryState.modified || addons.modified) {
      fetchShoppingCart();

      if (voucher.modified) {
        fetchVouchers();
      } else if (accessoryState.modified) {
        dispatch(resetModifyAccessories());
      } else if (addons.modified) {
        dispatch(resetModifyAddons());
      }
      if (addons.isAddBibSuccess) {
        dispatch(
          setBibDisplayData({
            status: 'success',
            hintMessage: t('BIB_APPLIED'),
            isApplied: true
          })
        );
      }
    }
  }, [voucher.modified, accessoryState.modified, addons.modified]);

  useEffect(() => {
    if (cart.checkoutError) {
      dispatch(clearCheckoutError());
    }
  }, []);

  const getOrderUpdateRulesData = (selectedAddonIds?: string[]) => {
    const { productOrderItemId, productId } = productOrder;

    return {
      productId,
      productOrderItemId,
      selectedAddonIds: selectedAddonIds || addons.selectedAddons,
      promoOrderDetails: {
        ...promoOrderDetails,
        sourcePlan: user?.selectedService?.offeringId
      }
    };
  };

  const showSopTradeInNoSpecIdPopup = () => {
    setModalData({
      visible: true,
      title: t('GENERIC_ERROR_TITLE') as string,
      message: t('GENERIC_ERROR_MESSAGE') as string,
      onClose: () => {
        window.history.back();
      }
    });
  };

  const handleAddTradeIn = async item => {
    if (!tradeInProductSpecContainmentId) {
      return showSopTradeInNoSpecIdPopup();
    }
    const { productOrderItemId } = productOrder;
    const tradeInRulesData = getOrderUpdateRulesData();
    await dispatch(
      addTradeInToOrder({
        productOrderItemId,
        ...item,
        tradeInRulesData,
        addDefaultChildItemsImplicitly: isSop,
        tradeInProductSpecContainmentId
      })
    );
  };

  const handleRemoveTradeIn = async () => {
    await handleRemoveAddon(cartOrder?.tradeInDetails?.tradeInBo, true);
  };

  const handleRemovePxAddon = async item => {
    dispatch(
      pxAddonsCalculateDispatch(ACTION_TYPES.ADDONS.PX_ADDONS_UPDATE_LOADING)
    );
    const pxAddons = addons.pxAddons || [];

    // Remove the item and save to a new array
    const updatedAddons = pxAddons.filter(
      addon =>
        !(addon.id === item.productID && addon.variant.id === item.variantID)
    );

    let updatedMontlyBills = cartOrder.monthlyBills.filter(
      addon => addon?.variantID !== item.variantID
    );

    //For Recon, get the new pricing info and update the pricing info in the monthly bill section also
    if (productOrder?.type === ORDER.TYPE.RECON) {
      const updatedAddonsCalculatePayload = updatedAddons.map(item => ({
        id: item.variant.id,
        productId: item.id,
        action: 'add',
        quantity: 1,
        recurrable: item.variant.recurrable,
        items: []
      }));

      const response = updatedAddonsCalculatePayload?.length
        ? await eShopPxAddonsCalculatePayable({
            serviceNumber: formatServiceNumberForPxAddons(
              cart?.order?.mobile?.serviceId
            ),
            addonsList: updatedAddonsCalculatePayload
          })
        : {};
      const variants =
        response && Array.isArray(response.variants) ? response.variants : [];
      if (variants) {
        // Update the price for each remaining variant based on the response
        updatedMontlyBills = updatedMontlyBills.map(addon => {
          const matchedVariant = variants?.find(v => v.id === addon.variantID);
          if (matchedVariant) {
            return {
              ...addon,
              price: matchedVariant.price
            };
          }
          return addon;
        });
      }
    }

    dispatch(
      pxAddonsCalculateDispatch(ACTION_TYPES.ADDONS.PX_ADDONS_UPDATE, {
        pxAddons: updatedAddons
      })
    );
    dispatch(
      pxAddonsCalculateDispatch(ACTION_TYPES.ADDONS.PX_ADDON_CART_UPDATE, {
        monthlyBills: updatedMontlyBills
      })
    );
  };

  const handleRemoveAddon = async (
    item,
    removeTradeIn = false,
    isRemoveBib = false
  ) => {
    if (item.isPxAddon) {
      await handleRemovePxAddon(item);
      return;
    }

    const { productOrderItemId } = productOrder;

    let removedAddonIds = getAddonIdsByProductSpecContainmentID(
      item.productSpecContainmentID,
      availableAddons
    );
    const tradeInCID = item.productSpecPricing?.childPricingSchema?.id;
    removedAddonIds =
      removeTradeIn && tradeInCID
        ? [...removedAddonIds, tradeInCID]
        : removedAddonIds;

    const selectedAddonIds = selectedAddons.filter(
      addonId => !removedAddonIds.includes(addonId)
    );

    const includeTradeInCID =
      tradeInCIDValue &&
      !removeTradeIn &&
      !(selectedAddonIds || []).includes(tradeInCIDValue);

    // triple protect handling
    const mcafeeTriple = addonMcAfeeTripleCheck(item.cID);
    const runUpdateOrderRulesData = mcafeeTriple
      ? getOrderUpdateRulesData(selectedAddonIds)
      : null;

    await dispatch(
      removeAddonFromOrder({
        addon: item,
        productOrderItemId,
        promoOrderDetails,
        selectedAddonIds: includeTradeInCID
          ? [...selectedAddonIds, tradeInCIDValue]
          : selectedAddonIds,
        withPromoString: true,
        runUpdateOrderRulesData,
        removeWithBillingProduct:
          Boolean(mcafeeTriple) || (removeTradeIn && isSop),
        isRemoveBib
      })
    );

    dispatch(
      updateAddons({
        selectedAddons: includeTradeInCID
          ? [...selectedAddonIds, tradeInCIDValue]
          : selectedAddonIds,
        availableAddons,
        atomicAddons: addons.atomicAddons,
        selectedAddonsBo: addons.selectedAddonsBo,
        addons5G: addons.addons5G,
        bundleAddonsData: addons.bundleAddonsData,
        containmentIdData: addons.containmentIdData
      })
    );
  };

  const handleUpdateVoucher = (id, actionType) => {
    const payload = {
      productOrderReferenceNumber: productOrder.productOrderReferenceNumber,
      customerId,
      contactId,
      productOrderItemId: productOrder.productOrderItemId,
      voucherIds: [id],
      ...(isFeatureFlagEnabled(RED_PHASE) && {
        redemptionType: capitalizeFirstChar(
          applicableVouchers.find(voucher => voucher.idX8 === id).redemptionType
        )
      })
    };
    if (actionType === VOUCHER_ACTION.ADD) {
      dispatch(addVouchers(payload));
    } else if (actionType === VOUCHER_ACTION.REMOVE) {
      dispatch(removeVouchers(payload));
    }
  };

  const fetchOfflineStock = async groupId => {
    try {
      setOfflineStockLoading(true);
      const response = await fetchOfflineStockApi(groupId);
      return response[0].items;
    } catch (e) {
      return [];
    } finally {
      setOfflineStockLoading(false);
    }
  };

  const handleRemoveAccessories = async (item, isRemoveFromList = false) => {
    const productId = item.productID || getAccessoryToRemove(item, cartOrder);
    if (!productId) return;

    const result = await dispatch(
      removeAccessoriesFromOrder({
        customerId,
        productOrderItemId: productOrder.productOrderItemId,
        productId,
        eShopAccessoryId: abEshopOrderEnabled
          ? item?.selectedVariant?.deviceCode
          : undefined
      })
    );
    if (result && isRemoveFromList) {
      const accessories = accessoryState.selectedAccessories.filter(
        accessory => {
          return item?.selectedVariant?.id
            ? accessory.id !== item?.selectedVariant?.id
            : !item?.skus?.some(item => item.sku === accessory.deviceCode); // filter out accessory has been removed
        }
      );
      dispatch(storeSelectedAccessories(accessories));
    }
  };

  const handleCheckout = async () => {
    const hasRoiDataMismatch = getIsRoiMismatch(
      userInformation?.userDetails?.custId,
      roiValidatedData
    );

    if (hasRoiDataMismatch) {
      setCceMismatch(true);
    } else {
      if (!isMtposStockAvailable) {
        const deviceSku = cart?.order?.mobile?.device?.sku;
        const offlineStockAvail = await fetchOfflineStock(
          selectedProduct?.groupId
        );
        const availableItems = offlineStockAvail.map(sku => {
          return (
            sku?.skuId === deviceSku &&
            sku.data.find(
              item =>
                PRODUCT.STOCK_AVAILABLE_STATUS.includes(item?.stock_status) &&
                Number(item?.available_quantity) > 0 &&
                item?.store_id !== PRODUCT.STORE_ID.WARE_HOUSE
            )
          );
        });

        const itemCollectAtStore = availableItems.find(
          item => item?.store_id !== undefined
        );

        cart.itemCollectionAtStore = itemCollectAtStore;
        const isAllAccessoriesEnableForCollectAtStore = checkAccessoryForFulfilment(
          accessoryState.selectedAccessories
        );
        const removedItemDetails =
          (cartOrder?.freebies && cartOrder?.freebies.length > 0) ||
          (cartOrder?.accessories &&
            cartOrder?.accessories.length > 0 &&
            !isAllAccessoriesEnableForCollectAtStore) ||
          cartOrder?.tradeInDetails;
        if (itemCollectAtStore && removedItemDetails) {
          setOpenModal(true);
        } else {
          await handleCheckoutContinue();
        }
      } else {
        await handleCheckoutContinue();
      }
    }
  };

  const handleCheckoutContinue = async (clearAccessories = false) => {
    let updatedNotAvailableSkus = notAvailableSkus;
    if (clearAccessories) {
      const notAvailableAccessories = getAccessoriesItemsList({
        propKey: 'sku',
        propValue: 'skuId',
        selectedAccessoriesFromStore: accessoryState.selectedAccessories,
        cartOrder
      });
      updatedNotAvailableSkus = [
        ...notAvailableSkus,
        ...notAvailableAccessories
      ];
      const accessoryIDsToRemove = getAccessoriesItemsList({
        propKey: 'productID',
        selectedAccessoriesFromStore: accessoryState.selectedAccessories,
        cartOrder
      });
      setOpenModal(false);
      // api call to remove all accessories
      await handleRemoveAccessories(
        { productID: accessoryIDsToRemove, skus: notAvailableAccessories },
        true
      );
    }

    const {
      sim,
      productId,
      productOrderId,
      productOrderItemId,
      productOrderReferenceNumber
    } = productOrder;
    const removedContainedProducts = getCartModel()?.outOfStockItem?.dataSource?.map(
      item => ({ productID: item.productID })
    );
    const checkoutOrder = {
      shouldDeleteOpenOrder: abTestDeletePendingOrderForNewFlow,
      type: productOrder?.type,
      simOptions: sim,
      productId: productId,
      productOrderId: productOrderId,
      productOrderItemId: productOrderItemId,
      productOrderReferenceNumber: productOrderReferenceNumber,
      cart: cart,
      skipFulfillmentCheck: urlParams.creditLimitIndicator === 'INCREASED',
      removedContainedProducts,
      notAvailableSkus: updatedNotAvailableSkus,
      userInfo: {
        customerId,
        ...contact
      },
      isSimOnlyIPP: simOnlyIPP,
      itemSkuToOrderIdMapping: getItemSkuToOrderIdMapping(
        selectedProduct?.deviceCode,
        productOrder?.productOrderItemId
      ),
      selectedSimType: product?.selectedSimType || order?.selectedSimType,
      isNewBillingPreference: isNewBillingPreferenceApiEnabled
    };

    if (productOrder?.type === ORDER.TYPE.RECON) {
      dispatch(dispatchCheckoutRecon());
    }

    const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
    if (
      checkoutFlow === CONSTANTS.CHECKOUT_NEW_NUMBER &&
      uiamAccessToken &&
      uiamAccessToken.length > 0
    ) {
      dispatch(dispatchCheckoutExistingNew());
    }

    await dispatch(checkout(checkoutOrder));
  };

  const handleEmptyCart = async (showEmptyCart = false) => {
    if (productOrder) {
      const { productOrderId, productOrderItemId } = productOrder;
      skipEmptyCartPage.current = !showEmptyCart;
      await dispatch(
        emptyCart({
          customerId,
          productOrderId,
          productOrderItemId,
          skipEmptyCart: !showEmptyCart
        })
      );
      dispatch(clearDeliveryData());
    }
  };

  const handleRetrieveVoucher = () => {
    if (!isUserLoggedin) {
      return () =>
        window.open(getUIAMLoginUrl(navigation.SHOPPING_CART_PAGE), '_self');
    } else if (
      voucher.status === REQUEST_STATUS.FAILED &&
      !voucher?.vouchers?.length
    ) {
      return () => fetchVouchers();
    } else {
      return null;
    }
  };

  const handleOnChangePlan = async () => {
    await handleEmptyCart();
    skipEmptyCartPage.current = true;
    dispatch(clearSelectedAccessories());
    if (selectedProduct) {
      navigateToProductDetailsPage(selectedProduct, selectedPlan);
    } else {
      navigateToPlansPage(selectedPlan, cisUserInfo);
    }
  };

  const handleOnChangeNumber = async () => {
    return navigate('/number-selection');
  };

  const onTradeInTncClicked = async () => {
    window.open(CART.TNC_LINK, '_blank');
  };

  const handleChangePhone = async () => {
    await handleEmptyCart();
    skipEmptyCartPage.current = true;
    dispatch(clearSelectedAccessories());
    return navigate('/');
  };

  const handleChangeMainLine = async serviceId => {
    const selectedServiceId = Object.keys(user.mainLines).find(
      key => key === serviceId
    );
    const selectedMainLine = user.mainLines[selectedServiceId];

    const mainLineOptions = {
      customerId,
      mainLineGroupId: selectedMainLine.groupId,
      productOrderItemId: productOrder.productOrderItemId,
      productId: cart?.order?.mobile?.plan?.productId
    };

    try {
      await dispatch(updateMainLineNumber(mainLineOptions));

      const selectedService = user.products.find(
        product => product.serviceId === selectedMainLine.mainLineMSISDN
      );
      dispatch(setSelectedService(selectedService));
    } catch (e) {
      console.log('Exception occurred when updating main line number', e);
    }
  };

  const renderCartErrors = ignoreEmptyCart => {
    const allErrorMessagesFlattened = flattenNodes(allErrorMessage);
    const cartError = getCartError(
      cart.checkoutErrorData,
      allErrorMessagesFlattened
    );

    const placeholderValues = {
      plan: plan?.selectedPlan?.planName
    };
    const cartErrorDataModel =
      getCartErrorDataModel(cartError, placeholderValues) ||
      defaultGenericErrorModal;
    if (
      isFeatureFlagEnabled(CP9_FLOW) &&
      Object.values(CP_ERROR_CODES).includes(cartErrorDataModel?.errorCode)
    ) {
      const { navigateTo } = getCreditLimitErrorProps(cartErrorDataModel);
      return <CPModal errorData={cartErrorDataModel} navigateTo={navigateTo} />;
    }
    return (
      <GenericError
        title={cartErrorDataModel.title}
        errorText={cartErrorDataModel.errorText}
        primaryButtonText={cartErrorDataModel.primaryButtonText}
        secondaryButtonText={cartErrorDataModel.secondaryButtonText}
        onPrimaryClick={() => {
          window.location.href = cartErrorDataModel.primaryButtonLink;
        }}
        onSecondaryClick={() => {
          window.location.href = cartErrorDataModel.secondaryButtonLink;
        }}
        footerList={cartErrorDataModel.footerList}
      />
    );
  };

  const handleBrowseAccessories = () => {
    setTimeout(() => {
      scrollToSection('accessory-section');
    }, 0);
  };

  //------------- Bib handling -----------------------
  const onBibTradeInClicked = imei => {
    if (bib.tradeInBo?.productID) {
      return removeBibTradeIn();
    }

    addBibToOrder(imei);
  };

  const removeBibTradeIn = async () => {
    {
      dispatch(
        setBibDisplayData({
          status: 'loading',
          hintMessage: ''
        })
      );
      await handleRemoveAddon(bib?.tradeInBo, true, true);
    }
  };

  const addBibToOrder = async imei => {
    const { productOrderItemId } = productOrder;
    if (!bib) return;
    const { brand, model } = bib;
    if (cartOrder?.tradeInDetails?.tradeInBo) {
      return dispatch(
        setBibDisplayData({
          status: 'error',
          hintMessage: t('BIB_REMOVE_TRADE_IN_MESSAGE')
        })
      );
    }

    if (!tradeInProductSpecContainmentId) {
      return showSopTradeInNoSpecIdPopup();
    }

    dispatch(
      setBibDisplayData({
        status: 'loading',
        hintMessage: ''
      })
    );

    await addTradeInToOrder({
      productOrderItemId,
      brand,
      model,
      imei,
      isAddBib: true,
      addDefaultChildItemsImplicitly: isSop,
      tradeInProductSpecContainmentId
    });
  };

  //------------- End Bib handling -----------------------

  //--------------- Set loader --------------------
  const isSkeletonLoading =
    cart.skeletonLoading || onlineStockLoading || offlineStockLoading;

  const isLoading =
    addons.loading ||
    cart.loading ||
    cart.checkoutLoading ||
    accessoryState.status === REQUEST_STATUS.PENDING ||
    voucher.status === REQUEST_STATUS.PENDING ||
    order.loading ||
    !isMounted;
  const { Loader } = useLoader({
    loadings: [isLoading],
    skeletonLoadings: [isSkeletonLoading],
    options: {
      type: SkeletonType.sideBar
    }
  });

  //--------------- End Set loader --------------------

  //--------- Handle error --------------
  const needEmptyCart =
    !isLoading &&
    !isSkeletonLoading &&
    (!productOrder ||
      (!selectedProduct &&
        !isMobileShareOrSimOnlyPlan(selectedPlan) &&
        !specialPromo &&
        !btlOfferData) ||
      !cart.order) &&
    !skipEmptyCartPage.current;
  if (needEmptyCart) {
    if (ORDER.BTL_ORDER_ERRORS.includes(order?.error?.errorMsg)) {
      navigate(`/${navigation.BTL_ERROR_PAGE}`, { replace: true });
    } else {
      navigate('/empty-shopping-cart');
    }
    return null;
  } else if (cart.error || cart.checkoutError || cart.checkoutOrderError) {
    return renderCartErrors(cart.error || cart.checkoutError);
  }

  //--------- End Handle error --------------

  const getCartModel = (exceededDiscountAmount: string | number) => {
    const mobileSubscription = cart?.order?.mobile;
    const isPreviousIpp = cart?.order?.mobile?.existingDevice?.ippDetails;

    const { plan: cartOrderPlan } = mobileSubscription || {};

    const planDescription = createDescription({
      data: selectedPlan.data,
      sms: selectedPlan.sms,
      talktime: selectedPlan.talktime
    });
    const existingOrder = {
      device: isPreviousIpp && cart?.order?.mobile?.existingDevice?.model
    };
    let newCartOrder = {
      ...cartOrder,
      selectedService: productOrder?.service,
      monthlyBills: overrideRemovableAddons(
        cartOrder?.monthlyBills,
        removeAddonIDs
      ),
      selectedPlan: undefined
    };

    if (cartOrderPlan) {
      newCartOrder = {
        ...newCartOrder,
        selectedPlan: {
          name: cartOrderPlan.name,
          description: planDescription,
          price: cartOrderPlan.price,
          groupName: selectedPlan?.groupName
        }
      };
    }

    const callbacks = {
      handleChangeMainLine
    };

    const shoppingCartObject = {
      cartOrder: newCartOrder,
      selectedProduct: selectedProduct,
      orderType: productOrder?.type,
      mainLines: user?.mainLines,
      userSelectedServiceId: user?.selectedService?.serviceId,
      plans: plans,
      userProducts: user?.products,
      callbacks: callbacks,
      existingOrder: existingOrder,
      cisUser: cisUserInfo,
      unRemovableAddons: addons.unRemovableAddons,
      exceededDiscountAmount
    };

    return getShoppingCartDataModel(shoppingCartObject);
  };

  const render = () => {
    const isDeviceOutOfStock =
      selectedProduct &&
      !isSimOnlyPlan(selectedPlan) &&
      !onlineStockAvailable &&
      !isSkeletonLoading;
    if (isDeviceOutOfStock) {
      return (
        <OutOfStock
          selectedProduct={selectedProduct}
          onViewAvailableProduct={handleChangePhone}
        />
      );
    }

    const handleNotify =
      !acknowledgeNotification &&
      voucher?.vouchers?.length > applicableVouchers.length
        ? () => dispatch(setAcknowledgeNotification())
        : urlParams.creditLimitIndicator === CREDIT_LIMIT_INDICATOR.INCREASED
        ? () => {}
        : null;

    const scmVoucher = createShoppingCartDataModelVoucher({
      vouchers: applicableVouchers,
      isCheckoutPriceOffset: cartOrder?.totalCheckOutPrice === 0,
      cisUser: !!cisUserInfo,
      eshopVoucherEnabled: abTestEshopVoucherEnabled
    });
    const upfront = getUpfront({
      upfrontPriceCanBeOffset: cartOrder?.totalOriginUpfrontPriceCanBeOffset,
      tradeIn: cartOrder?.tradeInDetails,
      discount: getCartModel()?.discount,
      vouchers: scmVoucher?.dataSource,
      upfrontPriceCanBeOffsetForTradeIn:
        cartOrder?.totalOriginUpfrontPriceCanBeOffsetForTradeIn
    });
    const tradeInStatus = validateTradeIn({
      tradeIn: cartOrder?.tradeInDetails,
      isHomeDeliveryAvailable: isMtposStockAvailable,
      upfront,
      plan: selectedPlan,
      hasBibTradeIn: !!bib?.tradeInBo?.productID,
      hasDevice: Boolean(selectedProduct),
      fulfilment
    });

    const getNotifyIconType = () =>
      urlParams.creditLimitIndicator === CREDIT_LIMIT_INDICATOR.INCREASED
        ? 'Completed'
        : null;

    const handleAddAccessory = async (accessory: SelectedAccessory) => {
      const result = await dispatch(
        addAccessoriesFromOrder({
          customerId,
          productOrderItemId: productOrder.productOrderItemId,
          productSpecId: accessory.id,
          eshopAccessoryId: abEshopOrderEnabled
            ? accessory.deviceCode
            : undefined
        })
      );

      if (result) {
        dispatch(
          storeSelectedAccessories([
            ...(accessoryState.selectedAccessories ?? []),
            accessory
          ])
        );
      }
    };

    const {
      showAccessoriesRecommender,
      exceededDiscountAmount
    } = getAccessoriesRecommender(cartOrder);

    const browseAccessory =
      enableAccessoriesForShoppingCart === 'true' && showAccessoriesRecommender;

    return (
      <>
        <Spacing data-testid="shopping-cart-module">
          <Loader>
            <ShoppingCartModule
              data={{
                ...getCartModel(exceededDiscountAmount),
                bibTradeIn: cart.bibDisplayData,
                tradeIn: {
                  brands,
                  modelByBrand: filterModelByBrand(modelByBrand),
                  device: cartOrder?.tradeInDetails,
                  onAddTradeIn: handleAddTradeIn,
                  onRemoveTradeIn: handleRemoveTradeIn,
                  tradeInNotAvailable: !tradeInStatus.available
                },
                voucher: {
                  ...scmVoucher,
                  addVoucher: id => handleUpdateVoucher(id, VOUCHER_ACTION.ADD),
                  removeVoucher: id =>
                    handleUpdateVoucher(id, VOUCHER_ACTION.REMOVE),
                  retrieveVoucher: handleRetrieveVoucher(),
                  error: voucher.status === REQUEST_STATUS.FAILED,
                  link: {
                    url: applicableVouchers.length
                      ? process.env.GATSBY_VOUCHER_TERMS_AND_CONDITIONS_URL
                      : null
                  }
                }
              }}
              callbacks={{
                onChangePhone: handleChangePhone,
                onChangePlan: handleOnChangePlan,
                onChangeNumber: handleOnChangeNumber,
                onRemoveItem: handleRemoveAddon,
                onEmptyCart: () => handleEmptyCart(true),
                onCheckout: handleCheckout,
                onTradeInTncClicked,
                onNotify: handleNotify,
                onBibTradeInClicked,
                onBorwseAccessories: handleBrowseAccessories,
                onVoucherModalClose: () => {
                  resetVouchers();
                }
              }}
              configs={{
                styles: {
                  noBottomSpacing: true
                },
                loading: isLoading,
                showBibTradeIn: isBibAvailable({
                  plan: selectedPlan,
                  selectedProduct,
                  flowType: productOrder?.type,
                  eligibleBibGroupIds
                }),
                showTradeIn: isFeatureFlagEnabled(TRADEIN),
                showVoucher: isFeatureFlagEnabled(VOUCHER),
                browseAccessories: browseAccessory,
                showOutOfStock: true,
                toastIcon: getNotifyIconType(),
                stickyBoundaryElement: '.stickyBoundary'
              }}
              localisation={{
                TRADE_IN_PHRASE: t('TRADE_IN_PHRASE'),
                TRADE_IN_MESSAGE: tradeInStatus.message,
                VOUCHER_MESSAGE: null,
                NOTIFY_MESSAGE: toastMessage,
                ACTION_BUTTON_DISMISS: t('CLOSE')
              }}
            />
          </Loader>
        </Spacing>
        <ShoppingCartAccessories
          accessories={accessories}
          isMTPOSStockCacheEnabled={false}
          abTestingPending={abTestApiPending}
          handleAddAccessory={handleAddAccessory}
          handleRemoveAccessories={handleRemoveAccessories}
          enableAccessoriesForShoppingCart={enableAccessoriesForShoppingCart}
          isLoading={isSkeletonLoading}
          accessoryAvailability={accessoryAvailability.stock}
        />

        {cceMismatch && (
          <CCEMisMatchError
            proceedClicked={cceProceedClicked}
            isLoggedInUser={true}
          />
        )}
        {renderAmendedErrorPopup()}
        {renderError()}
        <CollectAtStoreModal
          data-testid="collect-store-modal"
          fulfillmentConfig={fulfilment}
          visible={openModal}
          ctaText={t('PROCEED')}
          onClose={() => setOpenModal(false)}
          onButtonClick={() => handleCheckoutContinue(true)}
        />
        {renderedModal}
      </>
    );
  };

  return render();
};
